import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useAuth } from '@/hooks/useAuth';
import { useImageUpload } from '@/hooks/useImageUpload';
import supabase from '@/lib/supabase';

const categories = ['자유', '검증요청', '피해사례', '정보공유', '질문'];

export default function BoardWrite() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { images, uploadError, uploadImage, removeImage, getImageUrls, setUploadError } = useImageUpload();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('자유');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [loading, user, navigate]);

  const getDisplayName = (): string => {
    if (!user) return '익명';
    const nickname = user.user_metadata?.nickname;
    if (nickname && typeof nickname === 'string' && nickname.trim()) {
      return nickname.trim();
    }
    if (user.email) {
      const atIndex = user.email.indexOf('@');
      return atIndex > 0 ? user.email.substring(0, atIndex) : user.email;
    }
    return '익명';
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('이미지 크기는 5MB 이하만 가능합니다.');
        continue;
      }
      await uploadImage(file);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInsertImageToContent = (url: string) => {
    const imageMarkdown = `\n![이미지](${url})\n`;
    setContent((prev) => prev + imageMarkdown);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    setError('');
    setSubmitting(true);

    try {
      const imageUrls = getImageUrls();

      const insertData: Record<string, unknown> = {
        title: title.trim(),
        content: content.trim(),
        author: getDisplayName(),
        category,
        image_url: imageUrls.length > 0 ? JSON.stringify(imageUrls) : null,
      };

      if (user) {
        insertData.user_id = user.id;
      }

      const { data, error: insertError } = await supabase
        .from('posts')
        .insert(insertData)
        .select()
        .single();

      if (insertError) throw insertError;

      if (data) {
        navigate(`/board/${data.id}`);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '게시글 등록 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-foreground-500">인증 확인 중...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Header />

      <main className="flex-1">
        <div className="bg-secondary-950">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">글쓰기</h1>
            <p className="text-sm text-foreground-400">커뮤니티에 새로운 글을 작성해보세요</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-foreground-200 p-6 md:p-8">
            <div className="mb-5">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">분류</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full bg-white text-foreground-950 text-sm px-4 py-2.5 rounded-md border border-foreground-200 focus:outline-none focus:border-primary-500 cursor-pointer"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
                className="w-full bg-white text-foreground-950 text-sm px-4 py-2.5 rounded-md border border-foreground-200 focus:outline-none focus:border-primary-500 placeholder:text-foreground-400"
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="내용을 입력하세요"
                rows={12}
                className="w-full text-sm text-foreground-950 bg-background-50 border border-foreground-200 rounded-md px-4 py-3 resize-none focus:outline-none focus:border-primary-500 placeholder:text-foreground-400"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-foreground-950 mb-2">이미지 첨부</label>
              <div className="border-2 border-dashed border-foreground-200 rounded-lg p-4 hover:border-primary-400 transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full flex flex-col items-center gap-2 py-4 cursor-pointer text-foreground-500 hover:text-primary-500 transition-colors"
                >
                  <i className="ri-image-add-line text-2xl" />
                  <span className="text-sm font-medium">이미지 업로드</span>
                  <span className="text-xs text-foreground-400">JPG, PNG, GIF (최대 5MB)</span>
                </button>

                {uploadError && (
                  <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <i className="ri-error-warning-line" />
                      {uploadError}
                    </p>
                  </div>
                )}

                {images.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {images.map((img) => (
                      <div key={img.path} className="relative group">
                        {img.uploading ? (
                          <div className="aspect-square bg-background-100 rounded-md flex items-center justify-center">
                            <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                          </div>
                        ) : (
                          <>
                            <img
                              src={img.url}
                              alt="업로드 이미지"
                              className="aspect-square object-cover rounded-md w-full"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-md transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                              <button
                                type="button"
                                onClick={() => handleInsertImageToContent(img.url)}
                                className="w-7 h-7 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-50 transition-colors"
                                title="본문에 삽입"
                              >
                                <i className="ri-file-copy-line text-xs text-foreground-700" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeImage(img.path)}
                                className="w-7 h-7 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-red-50 transition-colors"
                                title="삭제"
                              >
                                <i className="ri-close-line text-xs text-red-500" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-foreground-200">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line" />
                취소
              </button>
              <button
                type="submit"
                disabled={!title.trim() || !content.trim() || submitting}
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-200 disabled:text-foreground-400 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                {submitting ? '등록 중...' : (
                  <>
                    <i className="ri-check-line" />
                    등록하기
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}